// What Focus Refine says while the user waits — decided from SERVER facts only.
//
// The page used to run its own silence clock: three minutes with no visible
// change put up "Nothing has come back for a while … we don't know why" and a
// restart button. It was wrong every time it was measured (5 of 42 refine
// sessions on dev, 2026-09-24 → 26, and a card came in all 5: two had a sandbox
// turn running, two were waiting for a GitLab runner, one was the user's own
// time answering an ask), and its button killed the running turn on a healthy
// bridge (job 6396056). Three flaws, none fixable by a number: it read busy only
// when armed, it never saw a sandbox tool call (those ride `bridge_stream`), and
// it counted the user's own answer time as agent silence.
//
// The server knows what the page was guessing: `agent_turn` busy/idle (sandbox,
// servant and handoff) with the stretch's `since`, the `sandbox_status` phase
// with its `since`, and the tool each turn calls. A turn cannot stay busy and
// silent forever — the bridge kills a `claude` that writes nothing for 5
// minutes, the server's watchdog un-says a busy nobody runs, the servant has a
// wall clock — and a pipeline that dies before it connects is turned into
// `terminated` by the server's pipeline watch. So the only timers left here
// wait on a server FACT holding still (20 s of idle-with-nothing-open, 60 s of
// no workspace at all), on the page's OWN socket (a first connect that has not
// opened in 12 s, a dropped one that has stayed down 3 s — the one thing the
// page knows first-hand), or drive the display.

import { activityOf, type WaitActivity } from "../chat/agent-work";
import type { SessionStatus } from "../chat/session-status";
import type { SandboxPhase } from "../state/sandbox-status-store";

/** Server idle, workspace ready, nothing open — for this long — is "stopped". */
export const IDLE_SETTLE_MS = 20_000;
/** No workspace at all (phase `idle`), server idle — for this long. */
export const NO_WORKSPACE_SETTLE_MS = 60_000;
/** Busy this long adds "taking longer than usual" (~3% of turns). */
export const WORK_LONG_MS = 180_000;
/** Waiting for a runner this long says the workspace hasn't started yet. */
export const RUNNER_LONG_MS = 180_000;
/** A dropped socket must stay down this long before the page says so — a
 *  reconnect that lands in a second says nothing. */
export const OFFLINE_GRACE_MS = 3_000;
/** A FIRST connect that has not opened for this long says Loom can't be
 *  reached. Without it a socket that never opens leaves the page on "Loom is
 *  waking up…" and a spinner forever, with nothing saying why — the one real
 *  case the old silence clock caught. A page that loads says nothing: the
 *  connect takes a second or two. */
export const FIRST_CONNECT_GRACE_MS = 12_000;
/** How often the wait line re-renders its minutes. */
export const WAIT_TICK_MS = 15_000;

// The activity vocabulary lives beside the fold that keeps it
// (`chat/agent-work.ts`), which holds only a call that has a word.
export { activityOf, type WaitActivity } from "../chat/agent-work";

export interface WorkInfo {
  /** Busy for this long, or null when the server gave no start. */
  readonly elapsedMs: number | null;
  readonly long: boolean;
  readonly activity: WaitActivity | null;
}

export type WaitStatus =
  | { readonly kind: "none" }
  | { readonly kind: "offline" }
  | { readonly kind: "unreachable" }
  | {
      readonly kind: "starting";
      readonly phase: SandboxPhase;
      readonly elapsedMs: number | null;
      readonly runnerLong: boolean;
      /** The servant is answering during the boot window. */
      readonly working: WorkInfo | null;
    }
  | { readonly kind: "reconnecting" }
  | ({ readonly kind: "working" } & WorkInfo)
  | { readonly kind: "stopped"; readonly reason: "silence" | null }
  | { readonly kind: "noWorkspace" };

export interface WaitInput {
  readonly socket: SessionStatus;
  /** This mount had an open socket, lost it, and it has stayed down for
   *  `OFFLINE_GRACE_MS`. A first connect is never "lost". */
  readonly offlineSettled: boolean;
  /** This mount has a session to connect to, has NEVER had an open socket, and
   *  has kept trying for `FIRST_CONNECT_GRACE_MS`. */
  readonly unreachableSettled: boolean;
  readonly phase: SandboxPhase;
  /** When the phase began, browser clock. */
  readonly phaseSinceMs: number | null;
  readonly busyKnown: boolean;
  readonly busy: boolean;
  /** When the busy stretch began, browser clock. */
  readonly busySinceMs: number | null;
  /** The latest tool name of this stretch. */
  readonly tool: string | null;
  readonly lastStop: "silence" | null;
  /** `stopCandidateOf` has read "stopped" for `IDLE_SETTLE_MS`. */
  readonly stoppedSettled: boolean;
  /** `stopCandidateOf` has read "noWorkspace" for `NO_WORKSPACE_SETTLE_MS`. */
  readonly noWorkspaceSettled: boolean;
}

const STARTING: ReadonlySet<SandboxPhase> = new Set<SandboxPhase>([
  "triggering",
  "waiting-for-runner",
  "booting",
]);

const workOf = (input: WaitInput, nowMs: number): WorkInfo => {
  const elapsedMs =
    input.busySinceMs === null ? null : Math.max(0, nowMs - input.busySinceMs);
  return {
    elapsedMs,
    long: elapsedMs !== null && elapsedMs >= WORK_LONG_MS,
    activity: input.tool === null ? null : activityOf(input.tool),
  };
};

/**
 * The wait line, in ladder order. The page's terminal screens (applied,
 * `sandbox_dead`, tools, budget, a session that is gone or refused) and its
 * question screens outrank all of this and are decided elsewhere.
 */
export const waitStatusOf = (input: WaitInput, nowMs: number): WaitStatus => {
  if (
    input.socket === "expired" ||
    input.socket === "denied" ||
    input.socket === "idle_killed"
  ) {
    return { kind: "none" };
  }
  if (input.offlineSettled) return { kind: "offline" };
  if (input.unreachableSettled) return { kind: "unreachable" };
  // `suspended` is the idle kill (its own dialog owns it); `terminated` is the
  // stopped-workspace screen.
  if (input.phase === "terminated" || input.phase === "suspended") return { kind: "none" };
  if (input.phase === "degraded") return { kind: "reconnecting" };
  if (STARTING.has(input.phase)) {
    const elapsedMs =
      input.phaseSinceMs === null ? null : Math.max(0, nowMs - input.phaseSinceMs);
    const busy = input.busyKnown && input.busy;
    return {
      kind: "starting",
      phase: input.phase,
      elapsedMs,
      runnerLong:
        input.phase === "waiting-for-runner" &&
        !busy &&
        elapsedMs !== null &&
        elapsedMs >= RUNNER_LONG_MS,
      working: busy ? workOf(input, nowMs) : null,
    };
  }
  // `agentBusy` carries through a reconnect (the attach snapshot corrects it),
  // so a busy read before `busyKnown` is the last thing the server said.
  if (input.busy) return { kind: "working", ...workOf(input, nowMs) };
  if (input.phase === "ready" && input.stoppedSettled) {
    return { kind: "stopped", reason: input.lastStop };
  }
  if (input.phase === "idle" && input.noWorkspaceSettled) return { kind: "noWorkspace" };
  return { kind: "none" };
};

/**
 * When the wait line's next "longer than usual" / "hasn't started yet" turns
 * on, browser clock — or null when nothing is due. The page re-renders then, so
 * its `focus.wait` trace records what the user was shown, not what the wait
 * looked like when it began.
 */
export const nextWaitFlipMs = (input: WaitInput, nowMs: number): number | null => {
  const due: number[] = [];
  if (input.busy && input.busySinceMs !== null) due.push(input.busySinceMs + WORK_LONG_MS);
  if (input.phase === "waiting-for-runner" && input.phaseSinceMs !== null) {
    due.push(input.phaseSinceMs + RUNNER_LONG_MS);
  }
  const future = due.filter((t) => t > nowMs);
  return future.length === 0 ? null : Math.min(...future);
};

export interface StopCandidateInput {
  readonly socket: SessionStatus;
  readonly phase: SandboxPhase;
  readonly busyKnown: boolean;
  readonly busy: boolean;
  /** Anything that means a turn is still owed or the user holds the turn: a
   *  card, an open ask or preview, a held or unsent answer, a restart in
   *  flight or a seed waiting to go out. */
  readonly blocked: boolean;
}

/**
 * Which "nothing is coming" state the facts point at RIGHT NOW, before the
 * settle time. The page holds it for `IDLE_SETTLE_MS` / `NO_WORKSPACE_SETTLE_MS`
 * (`useHeldFor`) before it says so.
 */
export const stopCandidateOf = (
  input: StopCandidateInput,
): "stopped" | "noWorkspace" | null => {
  if (input.socket !== "open" || !input.busyKnown || input.busy || input.blocked) {
    return null;
  }
  if (input.phase === "ready") return "stopped";
  if (input.phase === "idle") return "noWorkspace";
  return null;
};
